// OpticFlow frontend
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from './hooks/useTheme';
import { PermissionsContext } from './context/PermissionsContext';
import FeaturePermissions from './pages/FeaturePermissions';
import Dashboard from './pages/Dashboard';
import Customers from './pages/Customers';
import OfficerDashboard from './pages/OfficerDashboard';
import QuickCollection from './pages/QuickCollection';
import CollectionHistory from './pages/CollectionHistory';
import AllCollections from './pages/AllCollections';
import MonthlyInsights from './pages/MonthlyInsights';
import TodayCollections from './pages/TodayCollections';
import Tools from './pages/Tools';
import Profile from './pages/Profile';
import Sidebar from './components/Sidebar';
import Login from './pages/Login';
import Header from './components/Header';
import Billing from './pages/Billing';
import Settings from './pages/Settings';
import api from './services/api';

// Complaint module pages
import AdminComplaints from './pages/AdminComplaints';
import AdminTechnicians from './pages/AdminTechnicians';
import TechnicianDashboard from './pages/TechnicianDashboard';
import TechnicianComplaints from './pages/TechnicianComplaints';
import TechnicianCustomers from './pages/TechnicianCustomers';
import CustomerHistory from './pages/CustomerHistory';
import ActivationLog from './pages/ActivationLog';
import AdvanceActivations from './pages/AdvanceActivations';
import MissingEntries from './pages/MissingEntries';
import Expenses from './pages/Expenses';
import DailyRecovery from './pages/DailyRecovery';
import Employees from './pages/Employees';
import Payroll from './pages/Payroll';
import EmployeeLeaves from './pages/EmployeeLeaves';
import MonthlyClosing from './pages/MonthlyClosing';
import BonusPool from './pages/BonusPool';
import Stock from './pages/Stock';
import InstallationRequests from './pages/InstallationRequests';
import OfficerPendings from './pages/OfficerPendings';
import PendingActivationPayments from './pages/PendingActivationPayments';

// Layout Component to handle Sidebar/Header for both Admin and Officer
const AppLayout = ({ children, userRole, pageTitle, isCollapsed, setIsCollapsed, isMobileOpen, setIsMobileOpen }) => (
  <div className="flex bg-gray-50 min-h-screen relative overflow-x-hidden">
    <Sidebar
      isCollapsed={isCollapsed}
      setIsCollapsed={setIsCollapsed}
      isMobileOpen={isMobileOpen}
      setIsMobileOpen={setIsMobileOpen}
      userRole={userRole}
    />
    <div className={`flex-1 min-w-0 transition-all duration-300 relative ${isCollapsed ? 'lg:ml-20' : 'lg:ml-64'} ml-0`}>
      <Header
        toggleSidebar={() => setIsMobileOpen(!isMobileOpen)}
        isCollapsed={isCollapsed}
        pageTitle={pageTitle}
        userRole={userRole}
      />
      <main className="pt-16 min-h-screen min-w-0">
        {children}
      </main>
    </div>
  </div>
);

// Protected Route Wrapper
const ProtectedRoute = ({ children, allowedRoles, userRole, ...layoutProps }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentRole, setCurrentRole] = useState(null);
  const [featurePermissions, setFeaturePermissions] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const res = await api.get('/users/me/');
        // Determine role
        let role = 'user';
        if (res.data.is_superuser) {
          role = 'admin';
        } else if (res.data.is_manager) {
          role = 'manager';
        } else if (res.data.is_officer && res.data.is_technician) {
          role = 'officer_technician';
        } else if (res.data.is_officer) {
          role = 'officer';
        } else if (res.data.is_technician) {
          role = 'technician';
        } else if (res.data.is_clerk) {
          role = 'clerk';
        }
        setCurrentRole(role);
        setFeaturePermissions(res.data.feature_permissions || {});
        setIsAuthenticated(true);
      } catch (err) {
        localStorage.removeItem('access_token');
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, []);

  if (loading) return <div className="flex items-center justify-center h-screen">Loading...</div>;

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check access: dual-role users can access both officer and technician routes
  if (allowedRoles) {
    const hasAccess = allowedRoles.includes(currentRole) ||
      (currentRole === 'officer_technician' && (allowedRoles.includes('officer') || allowedRoles.includes('technician')));
    if (!hasAccess) {
      const defaultRoutes = { admin: '/', manager: '/customers', officer: '/officer-dashboard', technician: '/technician-dashboard', officer_technician: '/officer-dashboard', clerk: '/activations' };
      return <Navigate to={defaultRoutes[currentRole] || '/login'} replace />;
    }
  }

  // Pass the detected role to the Layout, wrap with permissions context
  return (
    <PermissionsContext.Provider value={featurePermissions}>
      <AppLayout userRole={currentRole} {...layoutProps}>
        {children}
      </AppLayout>
    </PermissionsContext.Provider>
  );
};

function App() {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  return (
    <ThemeProvider>
    <Router>
      <Routes>
        <Route path="/login" element={<Login />} />
        
        {/* Admin only Routes */}
        <Route path="/" element={
          <ProtectedRoute allowedRoles={['admin']} pageTitle="Dashboard" isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} isMobileOpen={isMobileOpen} setIsMobileOpen={setIsMobileOpen}>
            <Dashboard />
          </ProtectedRoute>
        } />
        <Route path="/dashboard/today" element={
          <ProtectedRoute allowedRoles={['admin']} pageTitle="Today's Collections" isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} isMobileOpen={isMobileOpen} setIsMobileOpen={setIsMobileOpen}>
            <TodayCollections />
          </ProtectedRoute>
        } />
        <Route path="/dashboard/month" element={
          <ProtectedRoute allowedRoles={['admin']} pageTitle="This Month" isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} isMobileOpen={isMobileOpen} setIsMobileOpen={setIsMobileOpen}>
            <MonthlyInsights />
          </ProtectedRoute>
        } />
        <Route path="/customers" element={
          <ProtectedRoute allowedRoles={['admin', 'manager']} pageTitle="Customers" isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} isMobileOpen={isMobileOpen} setIsMobileOpen={setIsMobileOpen}>
            <Customers />
          </ProtectedRoute>
        } />
        <Route path="/customers/:id/history" element={
          <ProtectedRoute allowedRoles={['admin', 'manager', 'officer']} pageTitle="Customer History" isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} isMobileOpen={isMobileOpen} setIsMobileOpen={setIsMobileOpen}>
            <CustomerHistory />
          </ProtectedRoute>
        } />
        <Route path="/all-collections" element={
          <ProtectedRoute allowedRoles={['admin', 'manager']} pageTitle="All Collections" isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} isMobileOpen={isMobileOpen} setIsMobileOpen={setIsMobileOpen}>
            <AllCollections />
          </ProtectedRoute>
        } />
        <Route path="/billing" element={
          <ProtectedRoute allowedRoles={['admin', 'manager']} pageTitle="Billing & Invoices" isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} isMobileOpen={isMobileOpen} setIsMobileOpen={setIsMobileOpen}>
            <Billing />
          </ProtectedRoute>
        } />
        <Route path="/settings" element={
          <ProtectedRoute allowedRoles={['admin']} pageTitle="System Settings" isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} isMobileOpen={isMobileOpen} setIsMobileOpen={setIsMobileOpen}>
            <Settings />
          </ProtectedRoute>
        } />
        <Route path="/admin/feature-permissions" element={
          <ProtectedRoute allowedRoles={['admin']} pageTitle="Feature Access Control" isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} isMobileOpen={isMobileOpen} setIsMobileOpen={setIsMobileOpen}>
            <FeaturePermissions />
          </ProtectedRoute>
        } />
        <Route path="/tools" element={
          <ProtectedRoute allowedRoles={['admin', 'manager']} pageTitle="Tools" isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} isMobileOpen={isMobileOpen} setIsMobileOpen={setIsMobileOpen}>
            <Tools />
          </ProtectedRoute>
        } />
        
        {/* Officer Routes */}
        <Route path="/officer-dashboard" element={
          <ProtectedRoute allowedRoles={['officer', 'admin']} pageTitle="My Dashboard" isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} isMobileOpen={isMobileOpen} setIsMobileOpen={setIsMobileOpen}>
            <OfficerDashboard />
          </ProtectedRoute>
        } />
        <Route path="/collections/new" element={
          <ProtectedRoute allowedRoles={['officer', 'admin']} pageTitle="Quick Collection" isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} isMobileOpen={isMobileOpen} setIsMobileOpen={setIsMobileOpen}>
            <QuickCollection />
          </ProtectedRoute>
        } />
<Route path="/my-collections" element={
          <ProtectedRoute allowedRoles={['officer', 'admin']} pageTitle="My History" isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} isMobileOpen={isMobileOpen} setIsMobileOpen={setIsMobileOpen}>
            <CollectionHistory />
          </ProtectedRoute>
        } />
        
        <Route path="/officer-pendings" element={
          <ProtectedRoute allowedRoles={['officer', 'admin']} pageTitle="My Pendings" isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} isMobileOpen={isMobileOpen} setIsMobileOpen={setIsMobileOpen}>
            <OfficerPendings />
          </ProtectedRoute>
        } />

        {/* Technician Routes */}
        <Route path="/technician-dashboard" element={
          <ProtectedRoute allowedRoles={['technician', 'admin']} pageTitle="Technician Dashboard" isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} isMobileOpen={isMobileOpen} setIsMobileOpen={setIsMobileOpen}>
            <TechnicianDashboard />
          </ProtectedRoute>
        } />
        <Route path="/technician/complaints" element={
          <ProtectedRoute allowedRoles={['technician', 'admin']} pageTitle="My Complaints" isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} isMobileOpen={isMobileOpen} setIsMobileOpen={setIsMobileOpen}>
            <TechnicianComplaints />
          </ProtectedRoute>
        } />
        <Route path="/technician/customers" element={
          <ProtectedRoute allowedRoles={['technician', 'admin']} pageTitle="Area Customers" isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} isMobileOpen={isMobileOpen} setIsMobileOpen={setIsMobileOpen}>
            <TechnicianCustomers />
          </ProtectedRoute>
        } />

        {/* Admin Complaint Routes */}
        <Route path="/complaints" element={
          <ProtectedRoute allowedRoles={['admin', 'manager', 'clerk']} pageTitle="Complaint Management" isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} isMobileOpen={isMobileOpen} setIsMobileOpen={setIsMobileOpen}>
            <AdminComplaints />
          </ProtectedRoute>
        } />
        <Route path="/technicians" element={
          <ProtectedRoute allowedRoles={['admin', 'manager']} pageTitle="Technician Management" isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} isMobileOpen={isMobileOpen} setIsMobileOpen={setIsMobileOpen}>
            <AdminTechnicians />
          </ProtectedRoute>
        } />

        {/* Shared Profile - Uses Detected Role via AppLayout */}
        <Route path="/profile" element={
          <ProtectedRoute allowedRoles={['admin', 'manager', 'officer', 'technician', 'clerk']} pageTitle="My Profile" isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} isMobileOpen={isMobileOpen} setIsMobileOpen={setIsMobileOpen}>
            <Profile />
          </ProtectedRoute>
        } />

        {/* Activation Log - Clerk + Admin */}
        <Route path="/activations" element={
          <ProtectedRoute allowedRoles={['admin', 'clerk', 'manager']} pageTitle="Activation Log" isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} isMobileOpen={isMobileOpen} setIsMobileOpen={setIsMobileOpen}>
            <ActivationLog />
          </ProtectedRoute>
        } />

        {/* Pending Activation Payments */}
        <Route path="/activation-payments" element={
          <ProtectedRoute allowedRoles={['admin', 'clerk', 'manager']} pageTitle="Pending Payments" isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} isMobileOpen={isMobileOpen} setIsMobileOpen={setIsMobileOpen}>
            <PendingActivationPayments />
          </ProtectedRoute>
        } />

        {/* Advance Activations - Admin + Manager */}
        <Route path="/advance-activations" element={
          <ProtectedRoute allowedRoles={['admin', 'manager']} pageTitle="Advance Activations" isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} isMobileOpen={isMobileOpen} setIsMobileOpen={setIsMobileOpen}>
            <AdvanceActivations />
          </ProtectedRoute>
        } />

{/* Missing Collection Entries - Admin + Officer */}
        <Route path="/missing-entries" element={
          <ProtectedRoute allowedRoles={['admin', 'officer']} pageTitle="Missing Entries" isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} isMobileOpen={isMobileOpen} setIsMobileOpen={setIsMobileOpen}>
            <MissingEntries />
          </ProtectedRoute>
        } />

        {/* Daily Expenses - Admin + permitted Managers */}
        <Route path="/expenses" element={
          <ProtectedRoute allowedRoles={['admin', 'manager']} pageTitle="Daily Expenses" isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} isMobileOpen={isMobileOpen} setIsMobileOpen={setIsMobileOpen}>
            <Expenses />
          </ProtectedRoute>
        } />

        {/* Recovery Pending - Admin */}
        <Route path="/recovery-pending" element={
          <ProtectedRoute allowedRoles={['admin']} pageTitle="Daily Total Recovery" isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} isMobileOpen={isMobileOpen} setIsMobileOpen={setIsMobileOpen}>
            <DailyRecovery />
          </ProtectedRoute>
        } />

        {/* Employees - Admin */}
        <Route path="/employees" element={
          <ProtectedRoute allowedRoles={['admin', 'manager']} pageTitle="Employees" isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} isMobileOpen={isMobileOpen} setIsMobileOpen={setIsMobileOpen}>
            <Employees />
          </ProtectedRoute>
        } />

        {/* Payroll - Admin */}
        <Route path="/payroll" element={
          <ProtectedRoute allowedRoles={['admin', 'manager']} pageTitle="Payroll" isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} isMobileOpen={isMobileOpen} setIsMobileOpen={setIsMobileOpen}>
            <Payroll />
          </ProtectedRoute>
        } />

        {/* Employee Leaves - Admin, Manager, Clerk */}
        <Route path="/employee-leaves" element={
          <ProtectedRoute allowedRoles={['admin', 'manager', 'clerk']} pageTitle="Employee Leaves" isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} isMobileOpen={isMobileOpen} setIsMobileOpen={setIsMobileOpen}>
            <EmployeeLeaves />
          </ProtectedRoute>
        } />

        {/* Bonus Pool - Admin */}
        <Route path="/bonus-pool" element={
          <ProtectedRoute allowedRoles={['admin', 'manager']} pageTitle="Bonus Pool" isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} isMobileOpen={isMobileOpen} setIsMobileOpen={setIsMobileOpen}>
            <BonusPool />
          </ProtectedRoute>
        } />

        <Route path="/stock" element={
          <ProtectedRoute allowedRoles={['admin', 'officer', 'technician', 'officer_technician', 'manager']} pageTitle="Stock" isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} isMobileOpen={isMobileOpen} setIsMobileOpen={setIsMobileOpen}>
            <Stock />
          </ProtectedRoute>
        } />

        <Route path="/installation-requests" element={
          <ProtectedRoute allowedRoles={['admin', 'officer', 'technician', 'officer_technician', 'manager']} pageTitle="Installation Requests" isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} isMobileOpen={isMobileOpen} setIsMobileOpen={setIsMobileOpen}>
            <InstallationRequests />
          </ProtectedRoute>
        } />

        {/* Monthly Closing - Admin/Manager */}
        <Route path="/office/monthly-closing" element={
          <ProtectedRoute allowedRoles={['admin', 'manager']} pageTitle="Monthly Closing" isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} isMobileOpen={isMobileOpen} setIsMobileOpen={setIsMobileOpen}>
            <MonthlyClosing />
          </ProtectedRoute>
        } />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
    </ThemeProvider>
  );
}

export default App;
